import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Message } from 'components/JobGenerator/types';
import { copy, minifyHtml } from 'helpers/functions';
import { extractFirstTagAndRest, parseDefaultValue } from 'helpers/htmlParser';
import { getMarkedHtmlFromSelection } from 'helpers/getMarkedHtmlFromSelection';
import CopyButton from '../CopyButton';
import LoadingDots from 'components/Common/Loaders/LoadingDots';
import { R_ONE_FIELDS } from 'components/Output/utils';
import { areAllFieldsDefined } from 'helpers/areAllFieldsDefined';
import { ContextMenuType } from '../../../Output/types';
import ContextMenu from 'components/Common/ContextMenu';

export interface SectionRewriteData {
    selectedText: string;
    sectionKey: string | number;
    sectionMarkedHtml: string;
}

interface SectionsOutputProps {
    item: Message;
    fields: Array<keyof Message>;
    isGeneratingJob: boolean;
    onSectionRewrite?: (data: SectionRewriteData) => void;
}

const SectionsOutput: React.FC<SectionsOutputProps> = ({
    item,
    fields,
    isGeneratingJob,
    onSectionRewrite,
}) => {
    const parser = parseDefaultValue;
    const [sectionContextMenus, setSectionContextMenus] = useState<
        Record<string | number, ContextMenuType>
    >({});
    const sectionRefs = useRef<Record<string | number, HTMLDivElement | null>>({});

    const onCopy = (value: string) => {
        copy({ value: minifyHtml(value), noMessage: true });
    };

    const getContainingSection = useCallback((node: Node | null): HTMLDivElement | null => {
        if (!node) {
            return null;
        }
        for (const ref of Object.values(sectionRefs.current)) {
            if (ref && ref.contains(node)) {
                return ref;
            }
        }
        return null;
    }, []);

    const handleSectionContextMenu = async (
        e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
        sectionKey: string | number,
    ) => {
        const selection = window.getSelection();
        const selectionText = selection?.toString();
        if (!selectionText || !selection || selection.rangeCount === 0) {
            return;
        }

        // Check if selection is entirely within this section
        const range = selection.getRangeAt(0);
        const startSection = getContainingSection(range.startContainer);
        const endSection = getContainingSection(range.endContainer);
        const currentSection = sectionRefs.current[sectionKey];

        // Only show context menu if selection is entirely within this section
        if (!currentSection || startSection !== currentSection || endSection !== currentSection) {
            return;
        }

        e.preventDefault();
        let { pageX, pageY } = e;
        if (pageX + 350 > window.innerWidth) {
            pageX = window.innerWidth - 350;
        }

        if (pageY + 115 > window.innerHeight) {
            pageY = window.innerHeight - 115;
        }

        // Get marked HTML with [[SELECTION]] tag
        const sectionContainer = sectionRefs.current[sectionKey];
        let markedHtml = selectionText;

        if (sectionContainer) {
            markedHtml = getMarkedHtmlFromSelection(sectionContainer, range);
        }

        setSectionContextMenus((prev) => ({
            ...prev,
            [sectionKey]: {
                x: pageX,
                y: pageY,
                value: selectionText,
                markedHtml,
                show: true,
            },
        }));
    };

    const handleCloseSectionContextMenu = (sectionKey: string | number) => {
        setSectionContextMenus((prev) => ({
            ...prev,
            [sectionKey]: { ...prev[sectionKey], show: false },
        }));
    };

    const handleSectionRewrite = (sectionKey: string | number) => {
        const menu = sectionContextMenus[sectionKey];
        if (menu && onSectionRewrite) {
            onSectionRewrite({
                selectedText: menu.value,
                sectionKey,
                sectionMarkedHtml: menu.markedHtml,
            });
        }
        handleCloseSectionContextMenu(sectionKey);
    };

    if (isGeneratingJob) {
        return <LoadingDots />;
    }

    if (!areAllFieldsDefined<Message>(item, R_ONE_FIELDS)) {
        return <></>;
    }

    return fields.map((key) => {
        let value = '';
        const keyValue = item?.[key];

        if (typeof keyValue === 'string') {
            value = keyValue || '';
        }

        const { tagHtml, remainingHtml } = extractFirstTagAndRest(value, 'h1');
        const parsedTitle = tagHtml ? parser(tagHtml) : <h2>...</h2>;
        const parsedSection = parser(remainingHtml);
        const contextMenu = sectionContextMenus[key];

        return (
            <div
                key={key}
                ref={(el) => {
                    if (el) {
                        sectionRefs.current[key] = el;
                    }
                }}
                style={{ userSelect: 'text' }}
                onContextMenu={(e) => handleSectionContextMenu(e, key)}
            >
                <div className="r-one-output-section">
                    {parsedTitle}
                    <CopyButton onCopy={() => onCopy(remainingHtml)} className="r-one-copy-btn" />
                </div>
                <div>{parsedSection}</div>
                {contextMenu?.show &&
                    createPortal(
                        <ContextMenu
                            x={contextMenu.x}
                            y={contextMenu.y}
                            value={contextMenu.value}
                            onClose={() => handleCloseSectionContextMenu(key)}
                            onRewrite={() => handleSectionRewrite(key)}
                        />,
                        document.body,
                    )}
            </div>
        );
    });
};

export default SectionsOutput;
